import * as React from 'react';

import { pagesPaths, PagesPathsKeys } from '~/shared/constants/pages';

export type Flags = Record<PagesPathsKeys, { active: boolean }>;

type FlagsContextData = {
  flags: Flags;
  skipCheck: boolean;
};

const FlagsContext = React.createContext<FlagsContextData>(
  {} as FlagsContextData
);

type FlagsProviderProps = {
  children: React.ReactNode;
  flags?: Partial<Flags>;
  skipCheck?: boolean;
};

export function FlagsProvider({
  children,
  flags,
  skipCheck = false,
}: FlagsProviderProps) {
  const providerValue = React.useMemo(
    () => ({
      flags: {
        ...Object.keys(pagesPaths).reduce(
          (acc, curr) => ({ ...acc, [curr]: { active: true } }),
          {} as Flags
        ),
        ...flags,
      },
      skipCheck,
    }),
    [flags, skipCheck]
  );

  return (
    <FlagsContext.Provider value={providerValue}>
      {children}
    </FlagsContext.Provider>
  );
}

export function useFlags() {
  const context = React.useContext(FlagsContext);

  if (!context) {
    throw new Error('useFlags must be used within a FlagsProvider');
  }

  return context.flags;
}

type MountFlagsOptions = {
  skipCheck?: boolean;
};

export function mountFlags(options: MountFlagsOptions = {}): Flags {
  const defaultFlags = Object.keys(pagesPaths).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: { active: true },
    }),
    {} as Flags
  );

  if (options.skipCheck) {
    return defaultFlags;
  }

  return {
    ...defaultFlags,
    home: {
      active: process.env.PAGE_HOME_ENABLED !== 'false',
    },
    surveys: {
      active: process.env.PAGE_SURVEYS_ENABLED !== 'false',
    },
  };
}

type ShouldBlockAccessOptions = {
  skipCheck?: boolean;
};

export function shouldBlockAccess(
  path: string,
  options: ShouldBlockAccessOptions = {}
) {
  if (options.skipCheck) {
    return false;
  }

  const flags = mountFlags({ skipCheck: options.skipCheck });

  return Object.entries(pagesPaths).some(([key, value]) => {
    if (typeof value === 'string' && path === value) {
      return !flags[key as PagesPathsKeys].active;
    }

    // if (value instanceof RegExp && value.test(path)) {
    //   return !flags[key as PagesPathsKeys].active;
    // }

    return false;
  });
}
